import { Component, computed, signal } from "@angular/core";
import { DraftForm, OnResolve, DraftFormType } from "src/app/modules/ngx-draft";
import { Dict, IProjectInfo, ProjectStatus } from "@vierkant-software/types__api";
import { DateTime } from "luxon";
import { Globals } from "src/app/services/globals.service";
import { groupBy, toDict } from "src/util/array";
import { sortBy } from "src/util/sort";
import { GroupedOption } from "src/util/types/global";

type ResolveData = {projects: IProjectInfo[]};
type ProjectDetail = IProjectInfo & {statusLabel: string, statusClass: string};
import { IFrontendUser } from "../../contract.interfaces";

@Component({
    templateUrl: './projects.step.haml',
    styleUrls:   ['./projects.step.sass'],
    standalone:  false
})
export class ProjectsStep implements OnResolve<ResolveData>{
    readonly onResolve = (data: ResolveData) => this.resolvedData.set(data);

    @DraftForm() form: DraftFormType<IFrontendUser>;

    private readonly resolvedData = signal<ResolveData>(undefined);


    public readonly projects = computed<Dict<ProjectDetail>>(() => {
        const data = this.resolvedData();
        return toDict(data?.projects ?? [], x => x.ID, x => ({
            ...x,
            statusLabel: this.getStatusLabel(x.status),
            statusClass: this.getStatusClass(x.status)
        }));
    });


    public readonly projectOptions = computed<GroupedOption<string>[]>(() => {
        const data = Object.values(this.projects() ?? {});
        const groups = Object.entries(groupBy(data, "status"));
        return sortBy(
            groups,
            ([key, _]) => key,
            ([key, _]) => this.getStatusLabel(Number.parseInt(key, 10)).toLowerCase()
        ).map(([key, value]) => ({
            label: value.at(0)?.statusLabel ?? '',
            value: key,
            items: sortBy(value, x => x.statusLabel.toLowerCase()).map(x => ({label: x.title, value: x.ID})),
        }));
    });

    public readonly projectList = computed<ProjectDetail[]>(() => Object.values(this.projects() ?? {}));
    public readonly filteredProjects = signal<ProjectDetail[]>([]);


    filter = (term: string, values: IProjectInfo[]) => {
        if ([null, undefined, ""].includes(term?.trim())) return values;
        const words = term.toLowerCase().split(" ");
        return values.filter(x => words.every(word => x.title.toLowerCase().indexOf(word) >= 0));
    };


    public checkDay(day: DateTime){
        return day.diffNow().valueOf() < 0;
    }

    private getStatusLabel(val: ProjectStatus){
        if (val === ProjectStatus.none) return '';
        return Globals.projectStatusLabels[val] ?? '';
    }

    private getStatusClass(val: ProjectStatus){
        if (val === ProjectStatus.active)
            return 'Success';
        if (val === ProjectStatus.inactive)
            return 'Danger';
        if (val === ProjectStatus.completed)
            return 'Text';
        if (val === ProjectStatus.completedAutomatically)
            return 'Default';
        if (val === ProjectStatus.planning)
            return 'Info';
        if (val === ProjectStatus.pause)
            return 'Warning';
        return 'Default';
    }

}
